import { Service } from "typedi";
import { ContentScriptChannel } from "./ContentScriptChannel";
import { ContentScriptOutboundMessage } from "../../shared/ChannelProtocol";

export interface TabState {
    tabId: string;
    url: string;
    title: string;
    favicon?: string;
    active: boolean;
}

type TabStateListener = (event: ContentScriptOutboundMessage, state: TabState) => void;

/**
 * Tracks the state of the content script connected to *this* controller. For
 * cross-instance state (other tabs in other controllers) see the leader-only
 * aggregation that runs on top of InstanceCoordinator.
 */
@Service()
export class ContentScriptRegistry {
    private state: TabState | null = null;
    private readonly listeners = new Set<TabStateListener>();

    attach(channel: ContentScriptChannel): void {
        channel.onMessage((msg) => this.ingest(msg));
    }

    getState(): TabState | null {
        return this.state;
    }

    onEvent(listener: TabStateListener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private ingest(event: ContentScriptOutboundMessage): void {
        switch (event.kind) {
            case "register":
                this.state = {
                    tabId: event.tabId,
                    url: event.url,
                    title: event.title,
                    active: document.visibilityState === "visible"
                };
                break;
            case "url-change":
                if (this.state) {
                    this.state.url = event.url;
                    this.state.title = event.title;
                }
                break;
            case "title-change":
                if (this.state) this.state.title = event.title;
                break;
            case "favicon-change":
                if (this.state) this.state.favicon = event.favicon;
                break;
            case "snapshot-report":
                if (this.state) {
                    if (event.favicon) this.state.favicon = event.favicon;
                    this.state.active = event.active;
                }
                break;
            case "page-unload":
                // keep state; the controller tears down when the iframe dies anyway
                break;
            default:
                break;
        }

        if (!this.state) return;
        const snapshot = { ...this.state };
        this.listeners.forEach((l) => {
            try { l(event, snapshot); } catch (err) { console.error("[content-script-registry] listener error", err); }
        });
    }
}
